/**
 * Tiered claim envelope and shareability axes (v40 §2). Every claim about a
 * person carries its own provenance + voicing policy so downstream consumers
 * (card writer, critic) can voice each claim at the right confidence level
 * and reject ones that should never surface.
 *
 * Claims live on `PersonCoreBrief.facts` (declared + observed) and
 * `PersonCoreBrief.hypotheses` (inferred). Splitting by tier is a contract
 * enforced in post-processing: `inferred` claims that drift onto `facts` get
 * migrated to `observed` (or dropped) rather than failing the whole brief.
 *
 * Following the brief schema convention: LLM-driven fields stay as `z.string()`
 * with `.describe()` documenting the valid values, rather than letting Zod
 * hard-fail the whole brief on one bad enum.
 *
 * The schema serves three roles — LLM output parsing, domain type, and DB
 * persistence shape — unified into a single source of truth. LLM-side
 * leniency (id, asOf) is expressed via `.optional()`; post-processing stamps
 * the missing values.
 */

import { z } from "zod";

const ShareabilitySchema = z.object({
  voicing: z
    .string()
    .describe(
      "One of: quotable, abstractable, internal, sealed. How the card writer may voice this claim. `quotable` = verbatim fine. `abstractable` = describe in general terms (never reproduce intake specifics or raw enrichment numbers). `internal` = matching only, never voiced. `sealed` = never surfaced anywhere, including host-review UI.",
    ),
  review: z
    .string()
    .describe(
      "One of: host-ok, porter-only. Whether the host sees the claim itself (separate from its voicing in cards) in admin/review surfaces.",
    ),
  meshTravel: z
    .string()
    .describe(
      "One of: shareable, local-only. Whether the claim may flow to other Porters in the mesh.",
    ),
  selfView: z
    .string()
    .describe(
      "One of: visible, hidden-unless-asked. What the user sees in their own Porter UI.",
    ),
});

export type Shareability = z.infer<typeof ShareabilitySchema>;

const ClaimScopeSchema = z.object({
  eventIds: z.array(z.string()).nullish(),
  session: z.string().nullish(),
});

export const ClaimSchema = z.object({
  id: z
    .string()
    .optional()
    .default("")
    .describe(
      "Stable per-claim id (UUID). Stamped in post-processing — the LLM does not emit ids. Load-bearing: retraction, supersession, derivation pivot on it.",
    ),
  claim: z.string().describe("Natural-language content of the claim."),
  tier: z
    .string()
    .describe(
      "One of: declared (the person stated it, or it's public record) · observed (visible in third-party data with source) · inferred (synthesis across evidence). Voiced differently downstream. `inferred` claims live on `hypotheses[]`, never on `facts[]`.",
    ),
  source: z
    .string()
    .describe(
      "Origin of the claim. One of: linkedin, clay, intake, intake-rolling, porter-synthesis, porter-memory, host-edit, co-attendance, self-declared. Drives the shareability source baseline.",
    ),
  sourceRef: z
    .string()
    .nullish()
    .describe("URL, post id, intake question id — points back to the source."),
  asOf: z
    .string()
    .optional()
    .default("")
    .describe(
      "ISO date when the claim was captured/verified. Stamped in post-processing if absent.",
    ),
  validUntil: z
    .string()
    .nullish()
    .describe("ISO date; after this, treat the claim as stale."),
  category: z
    .string()
    .describe(
      "One of: professional, personal, ideological, family, health, finance. Drives the shareability category ceiling.",
    ),
  shareability: ShareabilitySchema.describe(
    "Proposed voicing/review/mesh/self-view policy. Clamped element-wise to (proposed, source baseline, category ceiling) in post-processing — the LLM never gets to loosen its own output.",
  ),
  confidence: z
    .string()
    .nullish()
    .describe(
      "One of: low, med, high. Required on `inferred`-tier hypotheses; omitted on facts.",
    ),
  derivedFrom: z
    .array(z.string())
    .optional()
    .default([])
    .describe(
      "Claim ids this claim was synthesized from. Used on hypotheses to trace back to evidence.",
    ),
  scope: ClaimScopeSchema.nullish().describe(
    "Event-scoped claims (most intake answers) name the events / sessions they apply to.",
  ),
});

export type Claim = z.infer<typeof ClaimSchema>;
